/**
 * "Last sign-in" on the Team page and the Platform staff section (OP#1897, 2026-10-18).
 *
 * Pure helpers, free of React and of path aliases, so `npm run test:unit` (node --test) can
 * load them directly.
 *
 * THE SERVER IS THE AUTHORITY (api/v2/operators.py + services/sign_in_activity.py). Each
 * operator row carries `sign_in`:
 *   status        "seen" (a sign-in was recorded), "none" (the whole window was read and
 *                 holds none) or "unknown" (the sign-in service could not be read);
 *   last_at       ISO UTC of the last sign-in, or null;
 *   window_start  ISO UTC start of the period "none" covers;
 *   window_days   whole days in that period;
 *   flag          null, "inactive_90d" or "never_signed_in".
 *
 * WHY "NEVER" CARRIES A WINDOW: the sign-in service keeps sign-in history for 90 days and
 * only started recording on 2026-10-16. "No sign-in recorded" therefore means "none in the
 * last 90 days" once the history is 90 days deep, and "none since <date>" before that. A bare
 * "Never" would claim more than anyone knows.
 *
 * FAIL OPEN: a missing `sign_in` (older server) or status "unknown" renders "unknown" with
 * no flag, never an error.
 */

export type SignInStatus = "seen" | "none" | "unknown";
export type SignInFlag = "inactive_90d" | "never_signed_in";

/** The `sign_in` object on an operator row (absent on servers before OP#1897). */
export interface SignIn {
  status: SignInStatus;
  last_at: string | null;
  window_start: string | null;
  window_days: number | null;
  flag: SignInFlag | null;
}

/** How the history depth reads once it is full: the realm keeps events for 90 days. */
export const RETENTION_DAYS = 90;

/** Badge text per flag. */
export const FLAG_LABEL: Record<SignInFlag, string> = {
  inactive_90d: "Inactive 90+ days",
  never_signed_in: "Never signed in",
};

/** Hover text per flag: what it means and what to do about it. */
export const FLAG_HINT: Record<SignInFlag, string> = {
  inactive_90d:
    "No sign-in for 90 days or more. Consider turning off their sign-in or removing them.",
  never_signed_in:
    "The account is over 30 days old and has never been used. Check they received their sign-in details, or remove it.",
};

/** The one rendering of a row's sign-in: visible text, hover text, and the flag (if any). */
export interface SignInView {
  /** e.g. "3d ago", "Never (in the last 90 days)", "unknown". */
  text: string;
  /** Tooltip: the exact local time, or what "never" / "unknown" covers. */
  title: string;
  flag: SignInFlag | null;
  /** Badge text for `flag`, or null. */
  flagLabel: string | null;
  flagHint: string | null;
}

/** Parse an API timestamp; a bare one is UTC (mirrors lib/format.parseApiTime). */
function parseTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const t = new Date(zoned).getTime();
  return Number.isNaN(t) ? null : t;
}

/** "just now" / "12m ago" / "5h ago" / "3d ago" (mirrors lib/format.relTime). */
export function relativeAgo(then: number, now: number): string {
  const secs = Math.round((now - then) / 1000);
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/** Calendar date in UTC, e.g. "16 Oct 2026" -- stable across time zones for the label. */
export function fmtWindowDate(iso: string | null | undefined): string {
  const t = parseTime(iso);
  if (t === null) return "the start of the history";
  const d = new Date(t);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** What "never" covers: the full retention, or the shorter history so far. */
export function neverLabel(s: Pick<SignIn, "window_days" | "window_start">): string {
  if ((s.window_days ?? 0) >= RETENTION_DAYS) return `Never (in the last ${RETENTION_DAYS} days)`;
  return `Never (since ${fmtWindowDate(s.window_start)})`;
}

/**
 * Render a row's sign-in. `absolute` formats the exact time for the tooltip (the caller
 * passes lib/format.fmtAbsolute, so tests can stay locale-free).
 */
export function signInView(
  s: SignIn | null | undefined,
  now: number,
  absolute: (iso: string) => string = (iso) => iso,
): SignInView {
  const unknown: SignInView = {
    text: "unknown",
    title: "Sign-in history could not be read from the sign-in service right now.",
    flag: null,
    flagLabel: null,
    flagHint: null,
  };
  if (!s || s.status === "unknown") return unknown;
  const flag = s.flag && s.flag in FLAG_LABEL ? s.flag : null;
  const withFlag = (v: Omit<SignInView, "flag" | "flagLabel" | "flagHint">): SignInView => ({
    ...v,
    flag,
    flagLabel: flag ? FLAG_LABEL[flag] : null,
    flagHint: flag ? FLAG_HINT[flag] : null,
  });
  if (s.status === "seen") {
    const t = parseTime(s.last_at);
    if (t === null || !s.last_at) return unknown;
    return withFlag({ text: relativeAgo(t, now), title: absolute(s.last_at) });
  }
  if (s.status === "none") {
    const full = (s.window_days ?? 0) >= RETENTION_DAYS;
    return withFlag({
      text: neverLabel(s),
      title: full
        ? `Sign-in history is kept for ${RETENTION_DAYS} days; no sign-in in that period.`
        : `Sign-in history starts on ${fmtWindowDate(s.window_start)}; no sign-in since then.`,
    });
  }
  return unknown;
}
